import { useState, useEffect } from "react";
import "./BannerCarousel.scss";

const BannerCarousel = ({ banners }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const [fadeIn, setFadeIn] = useState(true);
  
  // Auto-advance slides every 5 seconds
  useEffect(() => {
    if (!banners || banners.length <= 1 || !isAutoPlaying) return;
    
    const interval = setInterval(() => {
      handleSlideChange((prevIndex) => (prevIndex + 1) % banners.length);
    }, 7000);
    
    return () => clearInterval(interval);
  }, [banners, isAutoPlaying]);

  // Reset auto-play after 10 seconds of user interaction
  useEffect(() => {
    if (!isAutoPlaying) {
      const timeout = setTimeout(() => {
        setIsAutoPlaying(true);
      }, 10000);
      
      return () => clearTimeout(timeout);
    }
  }, [isAutoPlaying]);
  
  const handleSlideChange = (indexFn) => {
    setFadeIn(false);
    
    // Wait for fade out before changing the slide
    setTimeout(() => {
      setCurrentIndex(indexFn);
      setFadeIn(true);
    }, 300);
  };
  
  if (!banners || banners.length === 0) return null;
  
  const goToPrevious = () => {
    handleSlideChange((prevIndex) => (prevIndex === 0 ? banners.length - 1 : prevIndex - 1));
    setIsAutoPlaying(false);
  };
  
  const goToNext = () => {
    handleSlideChange((prevIndex) => (prevIndex + 1) % banners.length);
    setIsAutoPlaying(false);
  };
  
  const goToSlide = (index) => {
    if (index === currentIndex) return;
    handleSlideChange(() => index);
    setIsAutoPlaying(false);
  };
  
  const currentBanner = banners[currentIndex];
  
  return (
    <div className="banner-carousel">
      <div className="carousel-container">
        <div 
          className={`carousel-track ${fadeIn ? 'fade-in' : 'fade-out'}`} 
          style={{ transform: `translateX(-${currentIndex * 100}%)` }}
        >
          {banners.map((banner, index) => (
            <div key={banner._id} className="carousel-slide">
              <img src={banner.image} alt={`${banner.title || banner.stage} Banner`} />
              {banner.title && (
                <div className="slide-content">
                  <h2 className="slide-title">{banner.title}</h2>
                  {banner.description && <p className="slide-description">{banner.description}</p>}
                </div>
              )}
            </div>
          ))}
        </div>
        
        {banners.length > 1 && (
          <>
            <button className="carousel-button prev" onClick={goToPrevious}>
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="15 18 9 12 15 6"></polyline>
              </svg>
            </button>
            <button className="carousel-button next" onClick={goToNext}>
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="9 18 15 12 9 6"></polyline>
              </svg>
            </button>
            
            <div className="carousel-indicators">
              {banners.map((_, index) => (
                <button
                  key={index}
                  className={`indicator ${index === currentIndex ? "active" : ""}`}
                  onClick={() => goToSlide(index)}
                  aria-label={`Go to slide ${index + 1}`}
                />
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default BannerCarousel; 